import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import AllCategoriesSection from "@/components/AllCategoriesSection";
import { EditableText, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import {
  Sparkles,
  ArrowRight,
  ChefHat,
  Cake,
  UtensilsCrossed,
  Heart,
  Star,
  Users,
  Calendar,
  CheckCircle2,
  Clock,
  Flame,
  Wine,
  Leaf
} from "lucide-react";

const CulinarySalesPage = () => {
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const { editMode } = useAdmin();

  useEffect(() => {
    document.title = "phygital - For Private Chefs & Bakers";
  }, []);

  const culinaryFeatures = [
    {
      icon: Heart,
      title: "Client taste profiles",
      description: "Dietary restrictions, allergies, cuisine preferences, spice tolerance - all captured upfront"
    },
    {
      icon: Calendar,
      title: "Menu planning",
      description: "Share custom menus, get approvals, and track dietary needs for every event"
    },
    {
      icon: UtensilsCrossed,
      title: "Service customization",
      description: "In-home dinners, event catering, cake orders - manage it all in one place"
    },
    {
      icon: Users,
      title: "Guest management",
      description: "Track preferences for each guest at multi-person events"
    }
  ];

  const serviceTypes = [
    { name: "Private Chef", icon: ChefHat, desc: "In-home dining experiences" },
    { name: "Custom Cakes", icon: Cake, desc: "Birthday, wedding, celebration cakes" },
    { name: "Event Catering", icon: UtensilsCrossed, desc: "Corporate & private events" },
    { name: "Meal Prep", icon: Flame, desc: "Weekly meal prep services" }
  ];

  const preferenceExamples = [
    { category: "Dietary", items: ["Vegetarian", "Vegan", "Gluten-Free", "Keto", "Halal", "Kosher"] },
    { category: "Allergies", items: ["Nuts", "Shellfish", "Dairy", "Eggs", "Soy", "Wheat"] },
    { category: "Cuisine", items: ["Italian", "French", "Asian Fusion", "Mediterranean", "Southern", "Mexican"] },
    { category: "Preferences", items: ["Spicy", "Mild", "No Cilantro", "Extra Garlic", "Low Sodium", "Organic"] }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" variant="light" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
              <ChefHat className="w-3 h-3 mr-1" />
              Culinary
            </Badge>
            <Button
              onClick={() => setShowEarlyAccessModal(true)}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="join-pilot-btn"
            >
              Get Early Access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <EditableSection sectionId="culinary-hero" label="Culinary Hero" className={editMode ? "py-20 px-4 bg-gradient-to-br from-orange-50 via-white to-amber-50" : "py-20 px-4 bg-gradient-to-br from-orange-50 via-white to-amber-50"}>
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-orange-100 text-orange-700 border-orange-200">
            <Sparkles className="w-3 h-3 mr-1" />
            <EditableText sectionId="culinary-hero" field="badge" defaultValue="For Private Chefs & Bakers" className="inline" />
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
            <EditableText sectionId="culinary-hero" field="title_line1" defaultValue="Every guest's preferences," className="inline" /><br />
            <EditableText sectionId="culinary-hero" field="title_line2" defaultValue="perfectly captured" className="inline" />
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            <EditableText 
              sectionId="culinary-hero" 
              field="subtitle" 
              defaultValue="Stop asking the same questions. Know dietary restrictions, allergies, and taste preferences before you plan the menu."
              multiline
            />
          </p>
          <Button
            size="lg"
            onClick={() => setShowEarlyAccessModal(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white text-lg px-8 py-6 rounded-full"
          >
            <ChefHat className="w-5 h-5 mr-2" />
            Get Early Access
          </Button>
        </div>
      </EditableSection>

      {/* Service Types */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Built for every culinary service</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {serviceTypes.map((type) => {
              const Icon = type.icon;
              return (
                <Card key={type.name} className="text-center hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center mx-auto mb-3">
                      <Icon className="w-7 h-7 text-orange-600" />
                    </div>
                    <h3 className="font-semibold mb-1">{type.name}</h3>
                    <p className="text-sm text-gray-500">{type.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-4">Stop guessing. Start knowing.</h2>
          <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
            Every dietary restriction and preference captured before you start cooking
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {culinaryFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <Card key={feature.title} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6 flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{feature.title}</h3>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Preference Examples */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-4">What you'll know about every client</h2>
          <p className="text-gray-600 text-center mb-10">Detailed preferences collected automatically</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {preferenceExamples.map((cat) => (
              <div key={cat.category}>
                <h4 className="font-semibold mb-3 text-orange-600">{cat.category}</h4>
                <div className="flex flex-wrap gap-2">
                  {cat.items.map((item) => (
                    <span key={item} className="px-3 py-1.5 bg-orange-50 text-orange-700 rounded-full text-sm">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Case: Cake Orders */}
      <section className="py-16 px-4 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-amber-100 text-amber-700">
                <Cake className="w-3 h-3 mr-1" />
                For Bakers
              </Badge>
              <h2 className="text-2xl font-bold mb-4">Custom cake orders, simplified</h2>
              <p className="text-gray-600 mb-6">
                Know exactly what your client wants before they place the order. Flavor preferences, 
                allergies, design inspiration - all in one place.
              </p>
              <ul className="space-y-3">
                {[
                  "Flavor & filling preferences",
                  "Design inspiration boards",
                  "Allergy & dietary needs",
                  "Delivery/pickup preferences",
                  "Past order history"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-orange-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="border-b pb-4 mb-4">
                <p className="font-semibold">New Order: Sarah's Birthday Cake</p>
                <p className="text-sm text-gray-500">Due: March 15, 2026</p>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Flavor</span>
                  <span className="font-medium">Red Velvet</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Filling</span>
                  <span className="font-medium">Cream Cheese</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Dietary</span>
                  <span className="font-medium text-orange-600">Nut-Free</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Style</span>
                  <span className="font-medium">Floral, Elegant</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500">Inspo photos attached (3)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to elevate your culinary business?</h2>
          <p className="text-orange-100 mb-8 text-lg">
            Join our pilot program and help shape the future of personalized culinary experiences.
          </p>
          <Button
            size="lg"
            onClick={() => setShowEarlyAccessModal(true)}
            className="bg-white text-orange-600 hover:bg-orange-50 text-lg px-8 py-6 rounded-full"
          >
            <ChefHat className="w-5 h-5 mr-2" />
            Get Early Access
          </Button>
        </div>
      </section>

      {/* All Categories */}
      <AllCategoriesSection currentCategory="culinary" />

      {/* Footer */}
      <SharedFooter />

      {/* Early Access Modal */}
      <EarlyAccessModal
        open={showEarlyAccessModal}
        onOpenChange={setShowEarlyAccessModal}
        category="culinary"
      />
    </div>
  );
};

export default CulinarySalesPage;
